import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, Download, Trash2, AlertTriangle, CheckCircle2, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const when = (iso) => iso ? `${fmtDate(iso.slice(0, 10))} ${iso.slice(11, 16)}` : "—";

function missingCsv(st) {
  const esc = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const head = ["Name", "Category", "Brand", "Model", "Serial", "Location", "Assigned to", "Status"];
  const lines = [head.join(","), ...st.missing_assets.map((a) => [
    a.name, a.category, a.brand, a.model, a.serial_no, a.location, a.assigned_to, a.status,
  ].map(esc).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url; el.download = `stock-take-missing-${st.finished_at.slice(0, 10)}.csv`;
  document.body.appendChild(el); el.click(); el.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Past stock-takes (audit trail) with a details view + missing-items CSV. */
export function StockTakeHistoryDialog({ open, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["stock-takes"], queryFn: api.listStockTakes, enabled: open,
  });
  const { data: detail } = useQuery({
    queryKey: ["stock-take", selectedId],
    queryFn: () => api.getStockTake(selectedId),
    enabled: !!selectedId,
  });
  const delMut = useMutation({
    mutationFn: (id) => api.deleteStockTake(id),
    onSuccess: () => {
      toast.success("Stock-take record deleted");
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["stock-takes"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSelectedId(null); onClose(); } }}>
      <DialogContent data-testid="stocktake-history-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-5" /> Stock-take history
          </DialogTitle>
          <DialogDescription className="text-xs">
            Every finished stock-take is kept for audit — who ran it, when, and what was missing.
          </DialogDescription>
        </DialogHeader>

        {selectedId ? (
          <div data-testid="stocktake-detail">
            <Button variant="ghost" size="sm" className="mb-2 -ml-2" data-testid="stocktake-detail-back"
                    onClick={() => setSelectedId(null)}>
              <ArrowLeft className="size-3.5 mr-1" /> All stock-takes
            </Button>
            {!detail ? <p className="text-sm text-muted-foreground">Loading…</p> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                  <Meta label="Finished" value={when(detail.finished_at)} />
                  <Meta label="Run by" value={detail.run_by?.name || detail.run_by?.email || "—"} />
                  <Meta label="Scope" value={detail.scope_label} />
                  <Meta label="Result" value={`${detail.checked_count} / ${detail.total} checked`} />
                </div>
                {detail.notes && <p className="text-xs text-muted-foreground mb-3 italic">“{detail.notes}”</p>}
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ring-1 ring-inset ${
                    detail.missing_count ? "bg-red-50 text-red-700 ring-red-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}
                        data-testid="stocktake-detail-missing">
                    {detail.missing_count
                      ? <><AlertTriangle className="size-3" /> {detail.missing_count} missing</>
                      : <><CheckCircle2 className="size-3" /> All accounted for</>}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" data-testid="stocktake-pdf" onClick={() => api.openStockTakePdf(detail.id)}>
                      <FileText className="size-3.5 mr-1" /> PDF report
                    </Button>
                    {detail.missing_count > 0 && (
                      <Button variant="outline" size="sm" data-testid="stocktake-missing-csv" onClick={() => missingCsv(detail)}>
                        <Download className="size-3.5 mr-1" /> Missing CSV
                      </Button>
                    )}
                    {user?.role === "admin" && (
                      <Button variant="ghost" size="sm" className="text-red-600" data-testid="stocktake-delete"
                              disabled={delMut.isPending} onClick={() => delMut.mutate(detail.id)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {detail.missing_count > 0 && (
                  <div className="border border-border rounded-md overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="overline">Missing asset</TableHead>
                          <TableHead className="overline">Serial</TableHead>
                          <TableHead className="overline">Holder / Location</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.missing_assets.map((a) => (
                          <TableRow key={a.id} data-testid={`stocktake-missing-${a.id}`}>
                            <TableCell className="text-sm font-medium">
                              {a.name}
                              <div className="text-[10px] text-muted-foreground">{[a.category, a.brand, a.model].filter(Boolean).join(" · ")}</div>
                            </TableCell>
                            <TableCell className="mono text-xs">{a.serial_no || "—"}</TableCell>
                            <TableCell className="text-xs">{[a.assigned_to, a.location].filter(Boolean).join(" — ") || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </div>
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="stocktake-history-empty">
            No stock-takes recorded yet. Start one, scan your assets, then press “Finish stock-take”.
          </p>
        ) : (
          <div className="border border-border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="overline">Finished</TableHead>
                  <TableHead className="overline">Run by</TableHead>
                  <TableHead className="overline">Scope</TableHead>
                  <TableHead className="overline text-right">Checked</TableHead>
                  <TableHead className="overline text-right">Missing</TableHead>
                  <TableHead className="overline text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" data-testid={`stocktake-row-${r.id}`}
                            onClick={() => setSelectedId(r.id)}>
                    <TableCell className="mono text-xs">{when(r.finished_at)}</TableCell>
                    <TableCell className="text-sm">{r.run_by?.name || r.run_by?.email || "—"}</TableCell>
                    <TableCell className="text-xs">{r.scope_label}</TableCell>
                    <TableCell className="text-right mono text-xs">{r.checked_count} / {r.total}</TableCell>
                    <TableCell className="text-right">
                      <span className={`mono text-xs font-semibold ${r.missing_count ? "text-red-700" : "text-emerald-700"}`}>
                        {r.missing_count}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="size-7" data-testid={`stocktake-row-pdf-${r.id}`}
                              aria-label="PDF report"
                              onClick={(e) => { e.stopPropagation(); api.openStockTakePdf(r.id); }}>
                        <FileText className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-slate-50 p-2">
      <p className="overline">{label}</p>
      <p className="font-medium mt-0.5 truncate" title={value}>{value}</p>
    </div>
  );
}
